import "./__queries__/featuredContent";
import React, { useContext, useEffect } from "react";
import { useLocation } from "react-router";
import { FEATURE_FLAG_NAME } from "common/constants";
import { HELPSCOUT_BEACON_PAGE } from "common/constants";
import { useGetFeaturedContentForGuestQuery } from "../../generated/graphql";
import { spacing } from "../../stitches.config";
import { setTitle } from "../../utils/title";
import { AuthContext } from "../Authorization/AuthContext";
import { Grid } from "../Common/Layout";
import {
  SkipNavigation,
  SkipNavigationContent,
} from "../Common/SkipNavigation";
import { Title as TitleTypography } from "../Common/Typography";
import { FeaturedItem } from "../FeaturedContent/FeaturedItem";
import {
  FeaturedContentSection,
  FeaturedContentWrapper,
  GuestWrapper,
  HeaderWrapper,
  MapboxBackground,
  SectionContainer,
  SectionContent,
} from "./__styles__/Guest";
import { GuestLandingPageMap } from "./LandingPageMap";
import { Footer, Header } from "./Layout";
import { PublicBanner } from "./PublicBanner";
import { accountTitle, useHelpscoutBeacon } from "./utils";

const CHAMELEON_RANDOM_ID = "5d410d53-5b3a-4323-98a8-7922e4bbf3a9";

export type GuestLocationState = {
  openLoginModal?: boolean;
};

export const Guest = () => {
  const location = useLocation<GuestLocationState>();
  useHelpscoutBeacon(HELPSCOUT_BEACON_PAGE.HOME);
  const { account, isFeatureEnabled } = useContext(AuthContext);
  const title = account?.publicPortal.navBarTitle;

  useEffect(() => {
    // timeout is here just to make sure chmln is fully loaded
    const timeoutId = setTimeout(() => {
      window.chmln?.identify?.(CHAMELEON_RANDOM_ID, {});
    }, 2000);

    return () => clearTimeout(timeoutId);
  }, []);

  const isMerchandisingEnabled = isFeatureEnabled(
    FEATURE_FLAG_NAME.MERCHANDISING,
  );

  const { data: featuredContentData } = useGetFeaturedContentForGuestQuery({
    skip: !isMerchandisingEnabled,
  });

  const featuredContentSections = featuredContentData?.featuredContent ?? [];

  const hasFeaturedContent =
    featuredContentSections.length > 0 &&
    featuredContentSections.some(
      section => section.featuredItems && section.featuredItems.length > 0,
    );
  const shouldShowFeaturedContent =
    isMerchandisingEnabled && hasFeaturedContent;

  setTitle(`${title} | ${accountTitle()}`);

  return (
    <GuestWrapper>
      <HeaderWrapper>
        <SkipNavigation />
        <PublicBanner />
        <Header openLoginModal={location.state?.openLoginModal ?? false} />
      </HeaderWrapper>
      <SkipNavigationContent>
        <MapboxBackground hasFeaturedContent={hasFeaturedContent}>
          <GuestLandingPageMap hasFeaturedContent={hasFeaturedContent} />
        </MapboxBackground>
        {shouldShowFeaturedContent && (
          <FeaturedContentWrapper>
            <FeaturedContentSection>
              {featuredContentSections
                .filter(
                  section =>
                    section.featuredItems && section.featuredItems.length > 0,
                )
                .slice()
                .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0))
                .map(section => (
                  <SectionContainer key={section.id}>
                    <SectionContent>
                      <TitleTypography
                        size="small"
                        type="semiBold"
                        style={{
                          alignSelf: "flex-start",
                          paddingBottom: spacing.m.value,
                        }}
                      >
                        {section.title}
                      </TitleTypography>
                      <Grid columns={{ base: 1, M: 2 }} gap={spacing.l}>
                        {section.featuredItems
                          .slice()
                          .sort(
                            (a, b) =>
                              (a.displayOrder ?? 0) - (b.displayOrder ?? 0),
                          )
                          .map(item => (
                            <FeaturedItem
                              key={item.id}
                              id={item.id}
                              image={item.image}
                              title={item.title}
                              description={item.description}
                              link={item.link}
                              entityType={item.entityType}
                              entityId={item.entityId}
                            />
                          ))}
                      </Grid>
                    </SectionContent>
                  </SectionContainer>
                ))}
            </FeaturedContentSection>
            <Footer />
          </FeaturedContentWrapper>
        )}
      </SkipNavigationContent>
    </GuestWrapper>
  );
};

export default Guest;
